import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { type ConnectionResponse } from '@graphql-box/react';
import { ChevronRight } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { Loader } from '#components/Loader/index.tsx';
import { TextLink } from '#components/TextLink/index.tsx';
import { Route } from '#enums/routes.ts';
import { extractNodesFromConnectionResponse } from '#helpers/extractNodesFromConnectionResponse.ts';
import { formatSearchResults } from '#helpers/formatSearchResults.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { type SearchListing } from '#types.ts';
import { Results } from './Results.tsx';

export type SearchResultsAsYouTypeProps = {
  inputValue: string;
  setSearchFullScreen: (fullScreen: boolean) => void;
};

const ResultsContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.backgroundSecondary,
  borderBottomLeftRadius: theme.spacing(1),
  borderBottomRightRadius: theme.spacing(1),
  borderTop: '1px solid ' + theme.palette.grey[300],
  height: '100%',
  overflowY: 'scroll',
}));

const ScrollContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

const GET_SEARCH_RESULTS = "query GetSearchResults(\n  $after: String\n  $before: String\n  $first: Int = 20\n  $includeAdult: Boolean = false\n  $last: Int\n  $query: String!\n  $searchType: SearchType = MULTI\n) {\n  search(\n    after: $after,\n    before: $before,\n    first: $first,\n    includeAdult: $includeAdult,\n    last: $last,\n    query: $query,\n    searchType: $searchType,\n  ) {\n    edges {\n      cursor\n      node {\n        ...on Movie {\n          posterPath\n          movieStatus: status\n          overview\n          releaseDate\n          runtime\n          tagline\n          title\n          voteAverage\n          voteCount\n        }\n        ...on Person {\n          birthday\n          deathday\n          name\n          knownForDepartment\n          placeOfBirth\n          profilePath\n        }\n        ...on Tv {\n          firstAirDate\n          lastAirDate\n          name\n          numberOfSeasons\n          overview\n          posterPath\n          tagline\n          tvStatus: status\n          voteAverage\n          voteCount\n        }\n      }\n    }\n    pageInfo {\n      hasNextPage\n      hasPreviousPage\n      startCursor\n      endCursor\n    }\n    totalCount\n  }\n}\n";

export const SearchResultsAsYouType = ({ inputValue }: SearchResultsAsYouTypeProps) => {
  const { config } = useToolbelt();
  const theme = useTheme();

  const { execute, loading, result } = useQuery<ConnectionResponse<SearchListing>>(GET_SEARCH_RESULTS, {
    loading: true,
  });

  useEffect(() => {
    if (inputValue) {
      void execute({ variables: { query: inputValue } }, { data: { initiator: 'SearchResultsAsYouType' } });
    }

    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const items = formatSearchResults(extractNodesFromConnectionResponse<SearchListing>(result?.data, 'search'), config);
  const newSearchParams = new URLSearchParams(globalThis.location.search);
  newSearchParams.set('keyphrase', inputValue);

  return (
    <>
      <ResultsContainer>
        <ScrollContainer>
          {loading ? <Loader sx={{ marginTop: theme.spacing(6) }} /> : <Results items={items} />}
        </ScrollContainer>
      </ResultsContainer>
      {inputValue ? (
        <TextLink
          sx={{
            color: theme.mainTextSecondary,
            display: 'flex',
            marginTop: theme.spacing(1.5),
          }}
          to={`${Route.SearchResults}?${newSearchParams.toString()}`}
        >
          {`View all results for`}
          &nbsp;
          <strong>{inputValue}</strong>
          <ChevronRight />
        </TextLink>
      ) : undefined}
    </>
  );
};
